import type { ApiResult } from './types';
import { apiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';

export type ApplicationOption = { id: number; label: string; code?: string };

/** Applications available for intranet role admin (Extranet is excluded). */
export const APPLICATION_OPTIONS: ApplicationOption[] = [
  { id: 1, label: 'Intranet', code: 'INTRANET' },
  { id: 3, label: 'Intranet - Mobile App', code: 'MOBILE' },
];

export const INTRANET_APPLICATION_ID = 1;
export const INTRANET_MOBILE_APPLICATION_ID = 3;

export function primaryApplicationIdFromFlags(
  allowedOnIntranet: boolean,
  allowedOnIntranetMobile: boolean,
): number {
  if (allowedOnIntranet) return INTRANET_APPLICATION_ID;
  if (allowedOnIntranetMobile) return INTRANET_MOBILE_APPLICATION_ID;
  return INTRANET_APPLICATION_ID;
}

export function applicationFlagsFromId(applicationId?: number): {
  allowedOnIntranet: boolean;
  allowedOnIntranetMobile: boolean;
} {
  if (applicationId === INTRANET_MOBILE_APPLICATION_ID) {
    return { allowedOnIntranet: false, allowedOnIntranetMobile: true };
  }
  if (applicationId === INTRANET_APPLICATION_ID) {
    return { allowedOnIntranet: true, allowedOnIntranetMobile: false };
  }
  return { allowedOnIntranet: true, allowedOnIntranetMobile: false };
}

const EXCLUDED_ROLE_APPLICATION_CODES = new Set(['EXTRANET']);

/** Extranet is not offered when creating/updating intranet user roles. */
export function isSelectableRoleApplication(app: { id?: number; code?: string }): boolean {
  if (app.id === 2) return false;
  const code = (app.code ?? '').trim().toUpperCase();
  return code.length > 0 && !EXCLUDED_ROLE_APPLICATION_CODES.has(code);
}

function roleApplicationLabel(name: string, code: string): string {
  if (code === 'INTRANET') return 'Intranet';
  if (code === 'MOBILE') return 'Intranet - Mobile App';
  return name || code;
}

export function applicationCodeForId(applicationId: number): string {
  return APPLICATION_OPTIONS.find((a) => a.id === applicationId)?.code ?? 'INTRANET';
}

type ApplicationApiRow = {
  id: number;
  name: string;
  code: string;
  order_index?: number;
  orderIndex?: number;
};

export async function listApplications(): Promise<ApiResult<ApplicationOption[]>> {
  const res = await apiRequest<ApplicationApiRow[]>(`/v1/user/mstm-application`, { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };

  const mapped = (res.data ?? [])
    .map((r) => {
      const code = (r.code ?? '').trim().toUpperCase();
      return {
        id: r.id,
        label: roleApplicationLabel(r.name ?? '', code),
        code: code || undefined,
        orderIndex: (r.orderIndex ?? r.order_index) as number | undefined,
      };
    })
    .filter((r) => isSelectableRoleApplication(r))
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
    .map(({ id, label, code }) => ({ id, label, code }));

  return { success: true, data: mapped };
}

export type RoleStatusFilter = '' | 'Active' | 'Inactive';

export type ActiveRoleRow = {
  roleId: number;
  roleName: string;
  moduleId?: number;
  orderIndex: number;
  roleCode?: string;
};

type ActiveRoleApiRow = {
  role_id: number;
  role_name: string;
  module_id?: number;
  order_index: number;
  role_code?: string | null;
  roleCode?: string | null;
};

export async function listActiveRoles(): Promise<ApiResult<ActiveRoleRow[]>> {
  const res = await apiRequest<ActiveRoleApiRow[]>(`/v1/user/msth-role`, { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };

  const mapped: ActiveRoleRow[] = (res.data ?? [])
    .map((r) => ({
      roleId: r.role_id,
      roleName: r.role_name ?? '',
      moduleId: r.module_id,
      orderIndex: r.order_index ?? 0,
      roleCode: (r.role_code ?? r.roleCode ?? undefined)?.trim() || undefined,
    }))
    .filter((r) => r.roleName.trim().length > 0)
    .sort((a, b) => a.orderIndex - b.orderIndex);

  return { success: true, data: mapped };
}

export type RoleListItem = {
  roleId: number;
  roleName: string;
  description: string;
  active: boolean;
  assignedUserCount: number;
  applicationId?: number;
  applicationName?: string;
  roleCode?: string;
  metaCode?: string;
  isMeta?: boolean;
  allowUpdate?: boolean;
  allowDelete?: boolean;
};

type RoleSearchRequest = {
  data: {
    application_id?: number;
    role_id?: number;
    meta_data?: boolean;
    active?: boolean;
    role_name?: string;
  };
};

type RoleSearchResponse = {
  content: Array<{
    roleId: number;
    roleName: string;
    description?: string;
    allowUpdate?: boolean;
    id?: number;
    isMeta?: boolean;
    is_meta?: boolean;
    allowDelete?: boolean;
    allow_delete?: boolean;
    active?: boolean;
    assigned_user_count?: number;
    assignedUserCount?: number;
    application_id?: number;
    applicationId?: number;
    application_name?: string;
    applicationName?: string;
    application_code?: string;
    applicationCode?: string;
    role_code?: string;
    roleCode?: string;
    meta_code?: string;
    metaCode?: string;
  }>;
};

export async function searchRoles(input: {
  applicationId?: number;
  roleId?: number;
  roleName?: string;
  status?: RoleStatusFilter;
}): Promise<ApiResult<RoleListItem[]>> {
  const body: RoleSearchRequest = {
    data: {
      application_id: input.applicationId,
      role_id: input.roleId,
      role_name: input.roleName?.trim() || undefined,
      active: input.status === 'Active' ? true : input.status === 'Inactive' ? false : undefined,
    },
  };

  const res = await apiRequest<RoleSearchResponse>(`/v1/user/msth-role/details?pageNumber=0&pageSize=2147483647`, {
    method: 'POST',
    body,
  });
  if (!res.ok) return { success: false, message: res.message };

  const mapped: RoleListItem[] = (res.data.content ?? []).map((r) => ({
    roleId: r.roleId,
    roleName: r.roleName ?? '',
    description: r.description ?? '',
    active: Boolean(r.active),
    assignedUserCount: (r.assignedUserCount ?? r.assigned_user_count ?? 0) as number,
    applicationId: (r.applicationId ?? r.application_id) as number | undefined,
    applicationName: (r.applicationName ?? r.application_name ?? r.applicationCode ?? r.application_code) as string | undefined,
    roleCode: (r.roleCode ?? r.role_code) as string | undefined,
    metaCode: (r.metaCode ?? r.meta_code ?? r.roleCode ?? r.role_code) as string | undefined,
    isMeta: Boolean(r.isMeta ?? r.is_meta),
    allowUpdate: r.allowUpdate,
    allowDelete: r.allowDelete ?? r.allow_delete,
  }));

  return { success: true, data: mapped };
}

export type Permission = {
  permissionId: number;
  permissionCode: string;
  permissionName: string;
  description?: string;
  selected: boolean;
  moduleId?: number;
  permissionOrderIndex?: number;
};

export type PermissionGroup = {
  groupId: number;
  groupName: string;
  description?: string;
  groupOrderIndex?: number;
  permissions: Permission[];
};

type PermissionGroupApi = {
  group_name: string;
  group_id: number;
  description?: string;
  group_order_index?: number;
  permissions_list: Array<{
    permission_id: number;
    permission_code: string;
    permission_name: string;
    module_id?: number;
    permission_order_index?: number;
    selected_permission?: boolean;
    description?: string;
  }>;
};

export async function listMetaCodes(): Promise<ApiResult<string[]>> {
  const res = await apiRequest<string[]>(`/v1/user/msth-role/meta-codes`, { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };
  const codes = (res.data ?? []).map((c) => String(c).trim().toUpperCase()).filter(Boolean);
  return { success: true, data: codes };
}

export async function viewPermissionDetails(input: {
  allowedOnIntranet: boolean;
  allowedOnIntranetMobile: boolean;
  inputMode: 'CREATE' | 'UPDATE';
  roleId?: number;
}): Promise<ApiResult<PermissionGroup[]>> {
  const qs = new URLSearchParams();
  if (input.roleId != null) qs.set('roleId', String(input.roleId));
  qs.set('inputMode', input.inputMode);
  qs.set('allowed_on_intranet', String(input.allowedOnIntranet));
  qs.set('allowed_on_intranet_mobile', String(input.allowedOnIntranetMobile));

  const res = await apiRequest<PermissionGroupApi[]>(`/v2/user/msth-role/view-permission-details?${qs.toString()}`, {
    method: 'GET',
    headers: { applicationCode: 'INTRANET', 'application-code': 'INTRANET' },
  });
  if (!res.ok) return { success: false, message: res.message };

  const mapped: PermissionGroup[] = (res.data ?? []).map((g) => ({
    groupId: g.group_id,
    groupName: g.group_name,
    description: g.description,
    groupOrderIndex: g.group_order_index,
    permissions: (g.permissions_list ?? []).map((p) => ({
      permissionId: p.permission_id,
      permissionCode: p.permission_code,
      permissionName: p.permission_name,
      description: p.description,
      selected: Boolean(p.selected_permission),
      moduleId: p.module_id,
      permissionOrderIndex: p.permission_order_index,
    })),
  }));

  return { success: true, data: mapped };
}

export type OrderIndexRow = {
  roleId: number | null;
  roleName: string;
  orderIndex: number;
  selected: boolean;
};

type OrderIndexApi = { role_id: number | null; role_name: string; order_index: number; selected: boolean };

export async function viewOrderIndexes(input: {
  inputMode: 'CREATE' | 'UPDATE';
  roleId?: number;
}): Promise<ApiResult<OrderIndexRow[]>> {
  const qs = new URLSearchParams();
  if (input.roleId != null) qs.set('roleId', String(input.roleId));
  qs.set('inputMode', input.inputMode);

  const res = await apiRequest<OrderIndexApi[]>(`/v1/user/msth-role/create-order-index?${qs.toString()}`, { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };

  const mapped: OrderIndexRow[] = (res.data ?? []).map((r) => ({
    roleId: r.role_id,
    roleName: r.role_name,
    orderIndex: r.order_index,
    selected: Boolean(r.selected),
  }));
  mapped.sort((a, b) => a.orderIndex - b.orderIndex);
  return { success: true, data: mapped };
}

export type RoleDetails = {
  roleId: number;
  roleName: string;
  description: string;
  active: boolean;
  orderIndex: number;
  applicationId?: number;
  allowedOnIntranet?: boolean;
  allowedOnIntranetMobile?: boolean;
  metaRole?: boolean;
  metaCode?: string;
  assignedUserCount?: number;
  createdAt?: string;
  updatedAt?: string;
  createdByName?: string;
  updatedByName?: string;
  permissions: PermissionGroup[];
};

type RoleDetailsApi = {
  role_details: {
    role_id: number;
    role_name: string;
    role_description?: string;
    active?: boolean;
    order_index?: number;
    application_id?: number;
    applicationId?: number;
    allowed_on_intranet?: boolean;
    allowedOnIntranet?: boolean;
    allowed_on_intranet_mobile?: boolean;
    allowedOnIntranetMobile?: boolean;
    created_at?: string;
    updated_at?: string;
    created_by_name?: string;
    updated_by_name?: string;
    meta_role?: boolean;
    meta_code?: string;
    assigned_user_count?: number;
  };
  permission_details: PermissionGroupApi[];
};

export async function viewRoleDetails(roleId: number): Promise<ApiResult<RoleDetails>> {
  const res = await apiRequest<RoleDetailsApi>(`/v1/user/msth-role/view-role-details?roleId=${encodeURIComponent(String(roleId))}`, { method: 'GET' });
  if (!res.ok) return { success: false, message: res.message };

  const rd = res.data.role_details;
  const hasExplicitApplicationFlags =
    rd.allowedOnIntranet != null ||
    rd.allowed_on_intranet != null ||
    rd.allowedOnIntranetMobile != null ||
    rd.allowed_on_intranet_mobile != null;
  const legacyFlags = applicationFlagsFromId((rd.applicationId ?? rd.application_id) as number | undefined);
  const allowedOnIntranet = hasExplicitApplicationFlags
    ? Boolean(rd.allowedOnIntranet ?? rd.allowed_on_intranet)
    : legacyFlags.allowedOnIntranet;
  const allowedOnIntranetMobile = hasExplicitApplicationFlags
    ? Boolean(rd.allowedOnIntranetMobile ?? rd.allowed_on_intranet_mobile)
    : legacyFlags.allowedOnIntranetMobile;
  const applicationId = (rd.applicationId ?? rd.application_id) as number | undefined;
  const perms: PermissionGroup[] = (res.data.permission_details ?? []).map((g) => ({
    groupId: g.group_id,
    groupName: g.group_name,
    description: g.description,
    groupOrderIndex: g.group_order_index,
    permissions: (g.permissions_list ?? []).map((p) => ({
      permissionId: p.permission_id,
      permissionCode: p.permission_code,
      permissionName: p.permission_name,
      description: p.description,
      selected: Boolean(p.selected_permission),
      moduleId: p.module_id,
      permissionOrderIndex: p.permission_order_index,
    })),
  }));

  return {
    success: true,
    data: {
      roleId: rd.role_id,
      roleName: rd.role_name ?? '',
      description: rd.role_description ?? '',
      active: Boolean(rd.active),
      orderIndex: rd.order_index ?? 0,
      applicationId,
      allowedOnIntranet: Boolean(allowedOnIntranet),
      allowedOnIntranetMobile: Boolean(allowedOnIntranetMobile),
      metaRole: rd.meta_role,
      metaCode: rd.meta_code,
      assignedUserCount: rd.assigned_user_count,
      createdAt: rd.created_at,
      updatedAt: rd.updated_at,
      createdByName: rd.created_by_name,
      updatedByName: rd.updated_by_name,
      permissions: perms,
    },
  };
}

type CreateRoleRequest = {
  data: {
    permissions_list: Array<{ permission_id: number }>;
    allowed_on_intranet: boolean;
    allowed_on_intranet_mobile: boolean;
    name: string;
    description: string;
    code: string;
    active: boolean;
    order_index: number;
  };
};

export async function createRole(input: {
  allowedOnIntranet: boolean;
  allowedOnIntranetMobile: boolean;
  name: string;
  description: string;
  code: string;
  active: boolean;
  orderIndex: number;
  permissionIds: number[];
}): Promise<ApiResult<{ roleId?: number }>> {
  const body: CreateRoleRequest = {
    data: {
      allowed_on_intranet: input.allowedOnIntranet,
      allowed_on_intranet_mobile: input.allowedOnIntranetMobile,
      name: input.name.trim(),
      description: input.description ?? '',
      code: input.code.trim().toUpperCase(),
      active: input.active,
      order_index: input.orderIndex,
      permissions_list: input.permissionIds.map((id) => ({ permission_id: id })),
    },
  };

  const res = await apiRequest<{ id?: number; role_id?: number }>(`/v2/user/msth-role/create-role`, { method: 'POST', body });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: { roleId: (res.data.role_id ?? res.data.id) as number | undefined } };
}

type UpdateRoleRequest = {
  data: {
    roleId: number;
    permissions_list: Array<{ permission_id: number }>;
    allowed_on_intranet: boolean;
    allowed_on_intranet_mobile: boolean;
    tenant_id: number;
    name: string;
    description: string;
    code: string;
    active: boolean;
    order_index: number;
  };
};

export async function updateRole(input: {
  roleId: number;
  code: string;
  allowedOnIntranet: boolean;
  allowedOnIntranetMobile: boolean;
  tenantId?: number;
  name: string;
  description: string;
  active: boolean;
  orderIndex: number;
  permissionIds: number[];
}): Promise<ApiResult<void>> {
  const body: UpdateRoleRequest = {
    data: {
      roleId: input.roleId,
      allowed_on_intranet: input.allowedOnIntranet,
      allowed_on_intranet_mobile: input.allowedOnIntranetMobile,
      tenant_id: input.tenantId ?? 0,
      name: input.name.trim(),
      description: input.description ?? '',
      code: input.code.trim().toUpperCase(),
      active: input.active,
      order_index: input.orderIndex,
      permissions_list: input.permissionIds.map((id) => ({ permission_id: id })),
    },
  };

  const res = await apiRequest<{ STATUS?: string }>(`/v2/user/msth-role/update-role`, { method: 'PUT', body });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

type DeleteRoleResponse = {
  status?: string;
  message?: string;
  response_result?: unknown;
};

export async function deleteRole(roleId: number): Promise<ApiResult<void>> {
  const res = await apiRequest<DeleteRoleResponse>(`/v1/user/msth-role/delete/${encodeURIComponent(String(roleId))}`, {
    method: 'DELETE',
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) {
    return { success: false, message: dataPayloadErrorMessage(res.data) };
  }
  return { success: true, data: undefined };
}

